/**
 * Employee highlight palette — matches hr example/CRC_Organogram mock.
 * Backend stores #RRGGBB in employees.highlight_color (null = default / none).
 * Organogram CSV may still store legacy ids c1–c5 (see LEGACY_HIGHLIGHT_COLOR_IDS).
 */
export const EMPLOYEE_HIGHLIGHT_COLORS = [
  { id: 'default', hex: '#e2e8f0', accent: '#94a3b8', label: 'Default' },
  { id: 'green', hex: '#dcfce7', accent: '#22c55e', label: 'Green' },
  { id: 'blue', hex: '#dbeafe', accent: '#3b82f6', label: 'Blue' },
  { id: 'orange', hex: '#ffedd5', accent: '#f97316', label: 'Orange' },
  { id: 'purple', hex: '#f3e8ff', accent: '#a855f7', label: 'Purple' },
  { id: 'rose', hex: '#fee2e2', accent: '#f87171', label: 'Rose' }
];

/** Legacy organogram color ids from CSV import (CRC_Organogram v3.10). */
const LEGACY_HIGHLIGHT_COLOR_IDS = Object.freeze({
  c1: '#dcfce7',
  c2: '#dbeafe',
  c3: '#ffedd5',
  c4: '#f3e8ff',
  c5: '#fee2e2',
});

/** @param {string|null|undefined} value */
function isValidCssHexColor(value) {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value ?? '').trim());
}

/** Normalize stored value to a comparable hex or null (default / invalid swatch). */
export function normalizeHighlightColor(value) {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === '') return null;

  const bare = raw.startsWith('#') ? raw.slice(1) : raw;
  if (/^c[1-5]$/.test(bare)) {
    return LEGACY_HIGHLIGHT_COLOR_IDS[bare] ?? null;
  }

  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  return isValidCssHexColor(withHash) ? withHash.toLowerCase() : null;
}

/** Whether a swatch option is selected for the current draft value. */
export function isHighlightColorSelected(storedValue, optionHex) {
  const current = normalizeHighlightColor(storedValue);
  const option = normalizeHighlightColor(optionHex);
  if (option === '#e2e8f0') {
    return current === null || current === '#e2e8f0';
  }
  return current === option;
}

/** Value to write into draft/API when a swatch is picked. */
export function highlightColorFromSwatch(optionHex) {
  const hex = normalizeHighlightColor(optionHex);
  if (hex === '#e2e8f0' || hex === null) return '';
  return hex;
}

/** Row background hex for table/card views; null = use default zebra striping. */
export function getEmployeeRowHighlightHex(storedValue) {
  const hex = normalizeHighlightColor(storedValue);
  if (!hex || hex === '#e2e8f0') return null;
  return hex;
}

/** Saturated accent for dark-theme borders / stripes (maps stored pastel to visible accent). */
export function getEmployeeHighlightAccent(storedValue) {
  const hex = normalizeHighlightColor(storedValue);
  if (!hex) return '#94a3b8';
  const match = EMPLOYEE_HIGHLIGHT_COLORS.find((c) => normalizeHighlightColor(c.hex) === hex);
  return match?.accent ?? hex;
}

/** True when the stored value should tint rows/cards (not default / empty). */
export function hasEmployeeHighlight(storedValue) {
  return getEmployeeRowHighlightHex(storedValue) != null;
}

/** First stored value that maps to a real swatch (hex or legacy c1–c5). */
export function resolveEmployeeHighlightColor(...values) {
  for (const value of values) {
    if (hasEmployeeHighlight(value)) return value;
  }
  return '';
}

/** Palette label for read-only UI; unknown hex falls back to the raw value. */
export function getEmployeeHighlightLabel(storedValue) {
  const hex = normalizeHighlightColor(storedValue);
  if (!hex || hex === '#e2e8f0') return 'Default';
  const match = EMPLOYEE_HIGHLIGHT_COLORS.find((c) => normalizeHighlightColor(c.hex) === hex);
  return match?.label ?? hex;
}

/**
 * CSS custom properties for theme-aware fill + accent.
 * Set on the highlighted element (row / card); global CSS applies tint + stripe.
 * @returns {Record<string, string>|null}
 */
export function getEmployeeHighlightCssVars(storedValue) {
  const fill = getEmployeeRowHighlightHex(storedValue);
  if (!fill) return null;
  const accent = getEmployeeHighlightAccent(storedValue);
  return {
    '--crc-employee-highlight-fill': fill,
    '--crc-employee-highlight-accent': accent,
    '--crc-dt-row-accent': fill,
    '--crc-dt-row-accent-border': accent,
    '--crc-highlight-accent': fill,
    '--crc-highlight-accent-border': accent,
  };
}

/**
 * PrimeVue DataTable rowClass / rowStyle bindings.
 * @returns {{ class: string, style: Record<string, string>|undefined }}
 */
export function getEmployeeHighlightRowBindings(storedValue, extraClass = '') {
  if (!hasEmployeeHighlight(storedValue)) {
    return { class: extraClass || '', style: undefined };
  }
  const className = ['crc-dt-row--highlight', extraClass].filter(Boolean).join(' ');
  return {
    class: className,
    style: getEmployeeHighlightCssVars(storedValue),
  };
}

/** Inline style for a palette swatch (picker / chip). */
export function getEmployeeHighlightSwatchStyle(option) {
  const hex = option?.hex || '#e2e8f0';
  const accent = option?.accent || '#94a3b8';
  return {
    '--crc-highlight-accent': hex,
    '--crc-highlight-accent-border': accent,
    '--employee-swatch-bg': hex,
    '--employee-swatch-accent': accent,
  };
}
